/**
 * Manager Approval Inbox — unified queue of pending leaves, travel and
 * reimbursement requests for a manager to approve or reject in one place.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Calendar, Plane, Receipt, CheckCircle2, XCircle, Inbox, User } from 'lucide-react';
import { apiClient } from '../../services/api';
import { toast } from '../../hooks/use-toast';

const Card = ({ children, className = '' }) => (
  <div className={`bg-white border border-black/5 rounded-2xl ${className}`}>{children}</div>
);
const H1 = ({ eyebrow, title, sub }) => (
  <div className="mb-6">
    <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">{eyebrow}</span>
    <h1 className="mt-2 text-3xl sm:text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>{title}</h1>
    {sub && <p className="mt-1 text-sm text-[#0b1a2e]/60">{sub}</p>}
  </div>
);
const Btn = ({ variant = 'primary', className = '', ...rest }) => {
  const styles = {
    primary: 'bg-[#0f5c3d] text-white hover:bg-[#0b4a30]',
    ghost: 'border border-[#0f5c3d]/20 text-[#0f5c3d] hover:bg-[#f7fbf8]',
    reject: 'border border-red-200 text-red-600 hover:bg-red-50',
  }[variant];
  return <button {...rest} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${styles} ${className}`} />;
};

const TAB_META = [
  { key: 'all', label: 'All', icon: Inbox },
  { key: 'leaves', label: 'Leaves', icon: Calendar },
  { key: 'travel', label: 'Travel', icon: Plane },
  { key: 'reimbursements', label: 'Claims', icon: Receipt },
];

const ApprovalsInboxPage = () => {
  const [tab, setTab] = useState('all');
  const [data, setData] = useState({ leaves: [], travel: [], reimbursements: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await apiClient.get('/hr/approvals/inbox'); setData(r.data); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed to load', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const decideLeave = async (id, decision) => {
    try { await apiClient.patch(`/hr/leave-requests/${id}`, { decision }); toast({ title: `Leave ${decision}` }); load(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  const decideTravel = async (id, decision) => {
    try { await apiClient.post(`/hr/travel/${id}/decide`, { decision }); toast({ title: `Travel ${decision}` }); load(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  const decideReimb = async (id, decision) => {
    try { await apiClient.patch(`/hr/reimbursements/${id}/decide`, { decision }); toast({ title: `Claim ${decision}` }); load(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };

  const c = data.counts || {};
  const showLeaves = tab === 'all' || tab === 'leaves';
  const showTravel = tab === 'all' || tab === 'travel';
  const showReimb = tab === 'all' || tab === 'reimbursements';

  return (
    <div>
      <H1 eyebrow="Manager Inbox" title="Approvals queue" sub={data.scope === 'my_reports' ? 'Requests from your direct reports awaiting your call.' : 'All pending requests across the organisation.'} />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {TAB_META.map((t) => {
          const val = t.key === 'all' ? (c.total || 0) : (c[t.key] || 0);
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`inbox-tab-${t.key}`}
              className={`p-3 rounded-2xl border text-left transition ${tab === t.key ? 'border-[#0f5c3d] bg-[#f0f8f3]' : 'border-black/5 bg-white hover:bg-[#f7fbf8]'}`}
            >
              <t.icon className="w-4 h-4 text-[#0f5c3d] mb-1" />
              <div className="text-2xl font-bold text-[#0b1a2e]">{val}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 font-semibold">{t.label} pending</div>
            </button>
          );
        })}
      </div>

      {loading ? <Card className="p-16 text-center"><Loader2 className="w-6 h-6 inline animate-spin text-[#0f5c3d]" /></Card> : (
        <div className="space-y-4">
          {showLeaves && (
            <SectionCard title="Leave requests" icon={Calendar} count={data.leaves.length}>
              {data.leaves.length === 0 ? <Empty label="No pending leave requests" /> : (
                <table className="w-full text-sm">
                  <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
                    <tr><Th>Employee</Th><Th>Type</Th><Th>From</Th><Th>To</Th><Th className="text-right">Days</Th><Th>Reason</Th><Th /></tr>
                  </thead>
                  <tbody>{data.leaves.map((l) => (
                    <tr key={l.id} className="border-t border-black/5" data-testid={`inbox-leave-${l.id}`}>
                      <td className="p-2"><EmpBadge name={l.user_name} code={l.employee_code} /></td>
                      <td className="p-2 text-xs">{l.leave_type_name || '—'}</td>
                      <td className="p-2 text-xs">{l.from_date}</td>
                      <td className="p-2 text-xs">{l.to_date}</td>
                      <td className="p-2 text-right font-semibold">{l.days}</td>
                      <td className="p-2 text-xs text-[#0b1a2e]/70 max-w-[240px] truncate">{l.reason || '—'}</td>
                      <td className="p-2 text-right whitespace-nowrap">
                        <Btn variant="ghost" onClick={() => decideLeave(l.id, 'approved')} data-testid={`inbox-leave-approve-${l.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Approve</Btn>
                        <Btn variant="reject" onClick={() => decideLeave(l.id, 'rejected')} data-testid={`inbox-leave-reject-${l.id}`} className="ml-2"><XCircle className="w-3.5 h-3.5" /> Reject</Btn>
                      </td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </SectionCard>
          )}

          {showTravel && (
            <SectionCard title="Travel requests" icon={Plane} count={data.travel.length}>
              {data.travel.length === 0 ? <Empty label="No pending travel requests" /> : (
                <table className="w-full text-sm">
                  <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
                    <tr><Th>Employee</Th><Th>Purpose</Th><Th>Destination</Th><Th>Dates</Th><Th className="text-right">Estimate</Th><Th /></tr>
                  </thead>
                  <tbody>{data.travel.map((t) => (
                    <tr key={t.id} className="border-t border-black/5" data-testid={`inbox-travel-${t.id}`}>
                      <td className="p-2"><EmpBadge name={t.user_name} code={t.employee_code} /></td>
                      <td className="p-2 text-xs max-w-[220px] truncate">{t.purpose}</td>
                      <td className="p-2 text-xs">{t.destination_city}{t.destination_country && ` · ${t.destination_country}`}</td>
                      <td className="p-2 text-xs">{t.depart_date} → {t.return_date}</td>
                      <td className="p-2 text-right text-xs">{t.estimated_cost || '—'} {t.currency || ''}</td>
                      <td className="p-2 text-right whitespace-nowrap">
                        <Btn variant="ghost" onClick={() => decideTravel(t.id, 'approved')} data-testid={`inbox-travel-approve-${t.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Approve</Btn>
                        <Btn variant="reject" onClick={() => decideTravel(t.id, 'rejected')} data-testid={`inbox-travel-reject-${t.id}`} className="ml-2"><XCircle className="w-3.5 h-3.5" /> Reject</Btn>
                      </td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </SectionCard>
          )}

          {showReimb && (
            <SectionCard title="Reimbursement claims" icon={Receipt} count={data.reimbursements.length}>
              {data.reimbursements.length === 0 ? <Empty label="No pending claims" /> : (
                <table className="w-full text-sm">
                  <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
                    <tr><Th>Employee</Th><Th>Category</Th><Th>Date</Th><Th>Description</Th><Th className="text-right">Amount</Th><Th /></tr>
                  </thead>
                  <tbody>{data.reimbursements.map((r) => (
                    <tr key={r.id} className="border-t border-black/5" data-testid={`inbox-reimb-${r.id}`}>
                      <td className="p-2"><EmpBadge name={r.user_name} code={r.employee_code} /></td>
                      <td className="p-2 text-xs">{r.category_name || '—'}</td>
                      <td className="p-2 text-xs">{r.date}</td>
                      <td className="p-2 text-xs text-[#0b1a2e]/70 max-w-[240px] truncate">{r.description || '—'}</td>
                      <td className="p-2 text-right font-semibold">{r.amount}</td>
                      <td className="p-2 text-right whitespace-nowrap">
                        <Btn variant="ghost" onClick={() => decideReimb(r.id, 'approved')} data-testid={`inbox-reimb-approve-${r.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Approve</Btn>
                        <Btn variant="reject" onClick={() => decideReimb(r.id, 'rejected')} data-testid={`inbox-reimb-reject-${r.id}`} className="ml-2"><XCircle className="w-3.5 h-3.5" /> Reject</Btn>
                      </td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </SectionCard>
          )}
        </div>
      )}
    </div>
  );
};

const SectionCard = ({ title, icon: Icon, count, children }) => (
  <Card>
    <div className="p-3 border-b border-black/5 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70">
      <Icon className="w-4 h-4 text-[#0f5c3d]" /> {title}
      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#0f5c3d]/10 text-[#0f5c3d]">{count}</span>
    </div>
    {children}
  </Card>
);

const Th = ({ children, className = '' }) => (
  <th className={`text-left p-2 ${className}`}>{children}</th>
);

const Empty = ({ label }) => (
  <div className="p-8 text-center text-sm text-[#0b1a2e]/50">{label}</div>
);

const EmpBadge = ({ name, code }) => (
  <div className="flex items-center gap-2">
    <div className="w-7 h-7 rounded-full bg-[#0f5c3d] text-white text-[11px] font-semibold flex items-center justify-center">
      {(name || '?').charAt(0).toUpperCase()}
    </div>
    <div className="min-w-0">
      <div className="text-sm font-medium text-[#0b1a2e] truncate">{name || '—'}</div>
      <div className="text-[10px] font-mono text-[#0b1a2e]/50">{code || ''}</div>
    </div>
  </div>
);

export default ApprovalsInboxPage;
